import classNames from 'classnames';
import { getDistance } from 'helpers/distance';
import { formatCurrency } from 'helpers/formatNumber';
import { useCallback, useMemo, useState } from 'react';
import { Marker, Popup } from 'react-leaflet';
import { InputLocation, FlexContainer, Button, Txt, View } from 'components';
import { observer } from 'mobx-react-lite';
import { useSearchPropertyViewState } from '../../../../../../state';
import { useRecentProperties } from 'state';
import {
  useMapCenterHelper,
  useSearchMainStateHelpers,
} from '../../../../hooks';
import styles from './SearchResultsMap.module.scss';

/**
 * SearchResultsMap
 *
 * @return {JSX.Element}
 */
const SearchResultsMap = ({ className }) => {
  const searchPropertyViewState = useSearchPropertyViewState();
  const recentProperties = useRecentProperties();
  const {
    state: { searchBrowserFilterState, searchResultsState },
    handleSubmitWithLocation,
  } = useSearchMainStateHelpers();
  const [location, setLocation] = useState(() => ({
    ...searchBrowserFilterState.state.location,
  }));

  const handleSubmit = () => handleSubmitWithLocation(location);
  const handleLocation = ({ target: { value } }) => setLocation(value);
  const handleMap = useMapCenterHelper(searchBrowserFilterState.state.location);
  const handlePropertyClick = useCallback(
    (i) => () => {
      recentProperties.add(i.id);
      searchPropertyViewState.open(i);
    },
    [searchPropertyViewState, recentProperties]
  );

  const isCenterChanged = useMemo(
    () => getDistance(location, searchBrowserFilterState.state.location) > 2,
    [location, searchBrowserFilterState.state.location]
  );

  const isZoomChanged = useMemo(
    () =>
      location.bounds && // Only if bounds exists in current location
      (!searchBrowserFilterState.state.location.bounds || // if not bounds in the query
        Math.abs(
          // or if diagonal length changed
          getDistance(
            { lat: location.bounds.latMin, lng: location.bounds.lngMin },
            { lat: location.bounds.latMax, lng: location.bounds.lngMax }
          ) -
            getDistance(
              {
                lat: searchBrowserFilterState.state.location.bounds.latMin,
                lng: searchBrowserFilterState.state.location.bounds.lngMin,
              },
              {
                lat: searchBrowserFilterState.state.location.bounds.latMax,
                lng: searchBrowserFilterState.state.location.bounds.lngMax,
              }
            )
        ) > 2),
    [location.bounds, searchBrowserFilterState.state.location.bounds]
  );
  const isLocationChanged = isCenterChanged || isZoomChanged;

  return (
    <FlexContainer
      className={classNames(
        styles.SearchResultsMap,
        className,
        `${SearchResultsMap.displayName}`
      )}
      column
      flex
    >
      <Button
        className={styles.Button}
        onClick={handleSubmit}
        disabled={!isLocationChanged}
      >
        {isLocationChanged ? 'Search here' : 'Move to search'}
      </Button>
      <InputLocation
        className={styles.InputLocation}
        name="location"
        initialValue={location.bounds ? undefined : location}
        onChange={handleLocation}
        whenCreated={handleMap}
        minZoom={7}
        maxZoom={15}
        bounds={
          location.bounds
            ? [
                [location.bounds.latMin, location.bounds.lngMin],
                [location.bounds.latMax, location.bounds.lngMax],
              ]
            : undefined
        }
        // zoomControl={false}
      >
        {searchResultsState.state.map((i) => (
          <Marker
            key={i.id}
            position={{
              lat: i.data.address?.lat,
              lng: i.data.address?.long,
            }}
          >
            <Popup autoPan={false}>
              <FlexContainer
                column
                justifyContentCenter
                alignCenter
                className={styles.PopupContainer}
                onClick={handlePropertyClick(i)}
              >
                <Txt x>{i.data.address?.name}</Txt>
                {i.data.tenancyRentAmount ? (
                  <View>
                    <Txt xx>£{formatCurrency(i.data.tenancyRentAmount)}</Txt>
                    <Txt caption>/month</Txt>
                  </View>
                ) : null}
              </FlexContainer>
            </Popup>
          </Marker>
        ))}
      </InputLocation>
    </FlexContainer>
  );
};

SearchResultsMap.displayName = 'SearchResultsMap';

export default observer(SearchResultsMap);
